import QRCodeStyling from "qr-code-styling";
import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";

import { triggerBrowserDownload } from "utilities/document/fileDownload.utils";

import { btLogoTypeUrlMap } from "commonComponents/utilities/BTLogo/BTLogoDeprecated";

export interface IBTQRCodeRef {
    /** Downloads the QR code as a PNG file */
    download: (fileName?: string) => Promise<void>;
    /** Gets the QR code instance for advanced operations */
    getInstance: () => QRCodeStyling | null;
}

export interface IBTQRCodeProps {
    /** The URL or data to encode in the QR code */
    value: string;
    /** Width of the QR code in pixels */
    width?: number;
    /** Height of the QR code in pixels */
    height?: number;
    /** Whether to show the Buildertrend logo in the center */
    showLogo?: boolean;
    /** Logo size as a ratio of the QR code size (0.1 to 1.0) */
    logoSize?: number;
    /** Margin around the logo */
    logoMargin?: number;
    /** Whether to round the corners of the QR code */
    roundedCorners?: boolean;
    /** CSS class name to apply to the container */
    className?: string;
    /** Test ID for testing purposes */
    testId?: string;
}

/**
 * A modern QR code component using QR Code Styling library with Buildertrend branding.
 * Supports downloading and provides access to the underlying QR code instance via ref.
 */
export const BTQRCode = forwardRef<IBTQRCodeRef, IBTQRCodeProps>(
    (
        {
            value,
            width = 500,
            height = 500,
            showLogo = true,
            logoSize = 0.5,
            logoMargin = 16,
            roundedCorners = false,
            className,
            testId,
        },
        ref,
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const qrCodeRef = useRef<QRCodeStyling | null>(null);

        // Expose methods via ref
        useImperativeHandle(
            ref,
            () => ({
                download: async (fileName = "QR_Code") => {
                    if (!value) {
                        throw new Error("QR Code value is required");
                    }

                    if (!qrCodeRef.current) {
                        throw new Error("QR Code instance not initialized");
                    }

                    const blob = (await qrCodeRef.current.getRawData("png")) as Blob | null;
                    if (!blob || !(blob instanceof Blob)) {
                        throw new Error("Failed to generate QR code PNG");
                    }

                    triggerBrowserDownload(blob, `${fileName}.png`);
                },
                getInstance: () => qrCodeRef.current,
            }),
            [value],
        );

        useEffect(() => {
            if (!value) return;

            const logoUrl = showLogo ? btLogoTypeUrlMap["minimal"]["dark-blue-blue"] : undefined;

            const qrCodeInstance = new QRCodeStyling({
                width,
                height,
                data: value,
                image: logoUrl,
                type: "canvas",
                dotsOptions: {
                    type: roundedCorners ? "rounded" : "square",
                },
                cornersSquareOptions: {
                    type: roundedCorners ? "rounded" : "square",
                },
                cornersDotOptions: {
                    type: roundedCorners ? "dot" : "square",
                },
                imageOptions: logoUrl
                    ? {
                          crossOrigin: "anonymous",
                          margin: logoMargin,
                          imageSize: logoSize,
                      }
                    : undefined,
            });

            qrCodeRef.current = qrCodeInstance;

            const currentContainer = containerRef.current;
            if (currentContainer) {
                // Clear previous QR code
                currentContainer.innerHTML = "";
                qrCodeInstance.append(currentContainer);
            }

            return () => {
                if (currentContainer) {
                    currentContainer.innerHTML = "";
                }
            };
        }, [value, width, height, showLogo, logoSize, logoMargin, roundedCorners]);

        useEffect(() => {
            if (qrCodeRef.current && containerRef.current?.children.length === 0) {
                qrCodeRef.current.append(containerRef.current);
            }
        });

        return <div ref={containerRef} className={className} data-testid={testId} />;
    },
);

export default BTQRCode;
